import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useRtl } from "@ryde/redux";
import {
  PLURAL_KEY,
  SINGLE_KEY,
  useFormats,
  type IRideTableDto,
} from "@ryde/shared";

import { RIDE_BULK_PRINT } from "./bulk-operations.rides.constants";
import { getRidePrintVehicle } from "./bulk-print.rides.utils";
import { PrintRidesFlexBlock } from "./bulk-print.styled";

interface IBulkPrintRideInfoProps {
  ride: IRideTableDto;
}

/** Ride details above the stations table: the trip in the wide column, who carries it out in the narrow one. */
export const BulkPrintRideInfo: FC<IBulkPrintRideInfoProps> = ({ ride }) => {
  const { t, i18n } = useTranslation();
  const {
    getFormattedDateTimePrint,
    formatDayOfWeekTranslated,
    formatTimeHHmmNormalized,
    formatDateNormalized,
  } = useFormats();
  const { isRtl } = useRtl();

  return (
    <div className="info-block">
      <div className="info-column">
        <div>
          <div className="label">
            {getFormattedDateTimePrint(
              t("general.TIME"),
              t("general.DATE"),
              isRtl,
            )}
          </div>
          <PrintRidesFlexBlock>
            <div className="bold">
              {`${formatTimeHHmmNormalized(ride.rideStart)} ${formatDateNormalized(ride.rideStart)}`}
            </div>
            <div>
              {formatDayOfWeekTranslated(
                i18n.language,
                { weekday: "long" },
                "start",
                ride.rideStart,
              )}
            </div>
          </PrintRidesFlexBlock>
        </div>
        <div>
          <div className="label">{t(`rideType.${SINGLE_KEY}`)}</div>
          <div className="bold">{ride.rideTypeName}</div>
        </div>
        <div>
          <div className="label">{t(`passenger.${PLURAL_KEY}`)}</div>
          <div className="bold">{ride.metadata?.seatsCount ?? 0}</div>
        </div>
        <div>
          <div className="label">
            {[t("general.FROM"), t("general.TO")].join(
              RIDE_BULK_PRINT.partsDelimiter,
            )}
          </div>
          <PrintRidesFlexBlock>
            <div className="bold">
              {`${ride.originStationName} ${isRtl ? ">" : "<"} ${ride.destinationStationName}`}
            </div>
            <div>
              {t("ride.gridLabel.direction")} {ride.direction}
            </div>
          </PrintRidesFlexBlock>
        </div>
      </div>
      <div className="info-column">
        <div>
          <div className="label">{t(`supplier.${SINGLE_KEY}`)}</div>
          <div className="bold">
            {[ride.supplierName, ride.supplierId].join(
              RIDE_BULK_PRINT.partsDelimiter,
            )}
          </div>
        </div>
        <div>
          <div className="label">{t(`vehicle.${SINGLE_KEY}`)}</div>
          <div className="bold">{getRidePrintVehicle(ride)}</div>
        </div>
        <div>
          <div className="label">{t(`driver.${SINGLE_KEY}`)}</div>
          <div className="bold">{ride.driverName}</div>
        </div>
      </div>
    </div>
  );
};
